import React, { useEffect, useState, useContext, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  MenuItem,
  IconButton,
  Menu,
  Grid,
  Card,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";
import axiosInstance from "api/axiosInstance";
import MDTypography from "components/MDTypography";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS, TYPO } from "theme/tokens";

// 2026-05-25 MAS Coin 使用紀錄 — 截圖一 design
// 串現有 API: GET /users/coin-transactions
//   params: type, startDate, endDate, page, pageSize
//   resp: { items: [{createdAt, feature, reason, amount}], totalPages, total? }
export default function CoinUsageHistory() {
  const [typeFilter, setTypeFilter] = useState(""); // "" = 全部
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [anchorElPageSize, setAnchorElPageSize] = useState(null); // 2026-05-25 顯示 dropdown
  const [items, setItems] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("CoinUpgradeManager", "plan/CoinUpgradeManager/CoinUpgradeManager_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.CoinUpgradeManager || { Main: {}, Error: {} },
    [componentLanguageData]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        type: typeFilter || undefined,
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined,
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
        page,
        pageSize,
      };
      const res = await axiosInstance.get("/users/coin-transactions", { params });
      setItems(res.data.items || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || (res.data.items || []).length);
    } catch (err) {
      console.error(LANGUAGE.Error.HistoryFailed, err);
    } finally {
      setLoading(false);
    }
  }, [typeFilter, startDate, endDate, page, pageSize, LANGUAGE.Error.HistoryFailed]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // feature → 顯示文字
  const typeLabelMap = {
    RECHARGE: LANGUAGE.Main.HistoryTypeRecharge || "儲值",
    STRATEGY: LANGUAGE.Main.HistoryTypeStrategy || "策略生成",
    BACKTEST: LANGUAGE.Main.HistoryTypeBacktest || "回測",
    REGISTER: LANGUAGE.Main.HistoryTypeRegister || "註冊獎勵",
  };
  const descMap = {
    RECHARGE: LANGUAGE.Main.HistoryDescRecharge || "MAS Coin 儲",
    STRATEGY: LANGUAGE.Main.HistoryDescStrategy || "使用 MAS Coin 生成",
    BACKTEST: LANGUAGE.Main.HistoryDescBacktest || "使用 MAS Coin 回測",
    REGISTER: LANGUAGE.Main.HistoryDescRegister || "註冊獎勵",
  };
  const isPositive = (feature) => ["RECHARGE", "REGISTER"].includes(feature);

  const footerText = (LANGUAGE.Main.HistoryFooter || "顯示 {{from}}-{{to}} 筆，共 {{total}} 筆資料")
    .replace("{{from}}", total === 0 ? 0 : (page - 1) * pageSize + 1)
    .replace("{{to}}", Math.min(page * pageSize, total))
    .replace("{{total}}", total);

  // 共用 input 樣式（DatePicker / Select / TextField 都吃）
  // 用較廣的選擇器 .MuiInputBase-root，覆蓋 Outlined / Select 兩種底層 root
  const inputSx = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      backgroundColor: COLORS.white,
      ...TYPO.bodyS,
      minHeight: "56px", // 跟 MUI 預設 outlined input 高度同步，TextField+select 才不會塌
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: COLORS.grayLighter,
    },
    "& .MuiInputLabel-root": {
      ...TYPO.bodyS,
      color: COLORS.grayDark,
    },
    "& .MuiSelect-select": {
      // TextField select 內層 padding，保持文字垂直置中
      paddingTop: "16px",
      paddingBottom: "16px",
      display: "flex",
      alignItems: "center",
    },
  };

  return (
    <Box>
      {/* 篩選列：開始日期 / 結束日期 / 類別 */}
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} sm={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={LANGUAGE.Main.HistoryStartDate || "開始日期"}
              value={startDate}
              onChange={(v) => {
                setPage(1);
                setStartDate(v);
              }}
              format="YYYY-MM-DD"
              slotProps={{ textField: { fullWidth: true, sx: inputSx } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={LANGUAGE.Main.HistoryEndDate || "結束日期"}
              value={endDate}
              onChange={(v) => {
                setPage(1);
                setEndDate(v);
              }}
              format="YYYY-MM-DD"
              slotProps={{ textField: { fullWidth: true, sx: inputSx } }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            select
            fullWidth
            label={LANGUAGE.Main.HistoryType || "類別"}
            value={typeFilter}
            onChange={(e) => {
              setPage(1);
              setTypeFilter(e.target.value);
            }}
            sx={inputSx}
          >
            <MenuItem value="">{LANGUAGE.Main.HistoryTypeAll || "全部"}</MenuItem>
            <MenuItem value="RECHARGE">{typeLabelMap.RECHARGE}</MenuItem>
            <MenuItem value="STRATEGY">{typeLabelMap.STRATEGY}</MenuItem>
            <MenuItem value="BACKTEST">{typeLabelMap.BACKTEST}</MenuItem>
            <MenuItem value="REGISTER">{typeLabelMap.REGISTER}</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      {/* table card */}
      <Card sx={{ borderRadius: 2, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
        {/* table header bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderBottom: "1px solid #eef0f3",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 3, height: 18, background: COLORS.main, borderRadius: 1 }} />
            <MDTypography sx={{ ...TYPO.bodyM, fontWeight: 600, color: COLORS.black }}>
              {LANGUAGE.Main.HistoryTitle || "MAS Coin 使用紀錄"}
            </MDTypography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <MDTypography sx={{ ...TYPO.bodyXS, color: COLORS.grayDark }}>
              {LANGUAGE.Main.HistoryShow || "顯示"}
            </MDTypography>
            {/* 2026-05-25 pageSize selector — Box + Menu pattern，跟 top bar 語言選擇一致 */}
            <Box
              onClick={(e) => setAnchorElPageSize(e.currentTarget)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                px: 1.5,
                height: 32,
                borderRadius: "8px",
                border: "1px solid #e5e7eb",
                cursor: "pointer",
                ...TYPO.bodyXS,
                fontWeight: 500,
                color: COLORS.black,
                background: COLORS.white,
                transition: "all 0.15s ease",
                "&:hover": { borderColor: COLORS.grayMedian, background: COLORS.grayLightest },
              }}
            >
              <span style={{ lineHeight: 1 }}>{pageSize}</span>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
            </Box>
            <Menu
              anchorEl={anchorElPageSize}
              open={Boolean(anchorElPageSize)}
              onClose={() => setAnchorElPageSize(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 80,
                  borderRadius: "10px",
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.05)",
                  "& .MuiMenuItem-root": {
                    ...TYPO.bodyXS,
                    py: 1,
                    px: 2,
                    justifyContent: "center",
                    "&.Mui-selected": {
                      background: COLORS.lighter,
                      color: COLORS.main,
                      fontWeight: 600,
                    },
                    "&:hover": { background: COLORS.grayLightest },
                  },
                },
              }}
            >
              {[5, 10, 20, 50].map((n) => (
                <MenuItem
                  key={n}
                  selected={pageSize === n}
                  onClick={() => {
                    setPage(1);
                    setPageSize(n);
                    setAnchorElPageSize(null);
                  }}
                >
                  {n}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Box>

        {/* table — 2026-05-25 改用 CSS Grid 排版，column 寬度 100% 可控
            MUI Table tableLayout fixed + colgroup 在 TableHead 上失效，棄用 */}
        <Box>
          {/* header row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "22% 18% 40% 20%",
              px: 3,
              py: 1.5,
              borderBottom: "1px solid #f5f5f5",
            }}
          >
            <Box sx={{ ...TYPO.bodyXS, fontWeight: 600, color: COLORS.grayDark }}>
              {LANGUAGE.Main.HistoryColTime || "時間"}
            </Box>
            <Box sx={{ ...TYPO.bodyXS, fontWeight: 600, color: COLORS.grayDark }}>
              {LANGUAGE.Main.HistoryColType || "類別"}
            </Box>
            <Box sx={{ ...TYPO.bodyXS, fontWeight: 600, color: COLORS.grayDark }}>
              {LANGUAGE.Main.HistoryColDesc || "說明"}
            </Box>
            <Box sx={{ ...TYPO.bodyXS, fontWeight: 600, color: COLORS.grayDark, textAlign: "right" }}>
              {LANGUAGE.Main.HistoryColAmount || "MAS Coin"}
            </Box>
          </Box>

          {/* body rows */}
          {loading ? (
            <Box sx={{ py: 4, textAlign: "center", color: COLORS.grayDark, ...TYPO.bodyXS }}>
              Loading...
            </Box>
          ) : items.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center", color: COLORS.grayDark, ...TYPO.bodyXS }}>
              {LANGUAGE.Main.HistoryEmpty || "目前尚無使用紀錄"}
            </Box>
          ) : (
            items.map((row, idx) => {
              const pos = isPositive(row.feature);
              const amount = pos ? `+${row.amount}` : `-${Math.abs(row.amount)}`;
              // 修補：升級補償 feature=RECHARGE(enum 不另開值避免跨環境 migration)，但 reason 含「升級/退還」
              //   → 類別/說明顯示「升級退還」而非「儲值」，避免誤會是儲值
              const isUpgradeRefund =
                row.feature === "RECHARGE" && /升級|升级|退還|退还/.test(row.reason || "");
              const catLabel = isUpgradeRefund
                ? (LANGUAGE.Main.HistoryTypeUpgradeRefund || "升級退還")
                : (typeLabelMap[row.feature] || row.feature);
              const descLabel = isUpgradeRefund
                ? (LANGUAGE.Main.HistoryDescUpgradeRefund || "方案升級退還")
                : (descMap[row.feature] || row.reason || "—");
              return (
                <Box
                  key={idx}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "22% 18% 40% 20%",
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid #f5f5f5",
                    transition: "background 0.15s",
                    "&:hover": { backgroundColor: COLORS.grayLightest },
                  }}
                >
                  <Box sx={{ ...TYPO.bodyXS, color: COLORS.black }}>
                    {dayjs(row.createdAt).format("YYYY-MM-DD")}
                  </Box>
                  <Box sx={{ ...TYPO.bodyXS, color: isUpgradeRefund ? COLORS.main : COLORS.black, fontWeight: isUpgradeRefund ? 600 : 400 }}>
                    {catLabel}
                  </Box>
                  <Box sx={{ ...TYPO.bodyXS, color: COLORS.black }}>
                    {descLabel}
                  </Box>
                  <Box
                    sx={{
                      ...TYPO.bodyS,
                      fontWeight: 600,
                      color: pos ? COLORS.black : COLORS.error,
                      textAlign: "right",
                    }}
                  >
                    {amount}
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderTop: "1px solid #eef0f3",
          }}
        >
          <MDTypography sx={{ ...TYPO.bodyXS, color: COLORS.main, fontWeight: 500 }}>
            {footerText}
          </MDTypography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
            </IconButton>
            <Box
              sx={{
                minWidth: 32,
                height: 32,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                ...TYPO.bodyXS,
                fontWeight: 600,
                color: COLORS.black,
              }}
            >
              {page}
            </Box>
            <IconButton
              size="small"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
            </IconButton>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}

CoinUsageHistory.propTypes = {};
